import { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardActionArea,
  CardContent,
  Grid,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Alert,
  Button,
  CircularProgress,
  Stack
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import HistoryIcon from '@mui/icons-material/History';
import MapIcon from '@mui/icons-material/Map';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import LogoutIcon from '@mui/icons-material/Logout';
import { useNavigate } from 'react-router-dom';
import { useAgenda } from '../modules/visitas/hooks/useAgenda';
import { useVisitaAtiva } from '../modules/visitas/hooks/useVisitaAtiva';
import { CheckoutModal } from '../modules/visitas/components/CheckoutModal';
import { useQueryClient } from '@tanstack/react-query';

// AIDEV-NOTE: Hub de visitas — ponto de entrada com atalhos para agenda, historico e mapa,
// visita ativa com checkout rapido e lista de visitas planejadas para hoje.

const STATUS_LABEL: Record<string, string> = {
  planejada: 'Planejada',
  em_andamento: 'Em andamento',
  concluida: 'Concluída',
  cancelada: 'Cancelada'
};

const STATUS_COLOR: Record<string, 'default' | 'warning' | 'info' | 'success' | 'error'> = {
  planejada: 'default',
  em_andamento: 'info',
  concluida: 'success',
  cancelada: 'error'
};

function formatHora(iso: string): string {
  return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

export function VisitasList() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  const { data: agenda = [], isLoading: loadingAgenda } = useAgenda();
  const { data: visitaAtiva, isLoading: loadingAtiva } = useVisitaAtiva();

  const handleCheckoutSuccess = () => {
    setCheckoutOpen(false);
    qc.invalidateQueries({ queryKey: ['visita-ativa'] });
    qc.invalidateQueries({ queryKey: ['agenda'] });
  };

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Visitas
      </Typography>

      {/* Visita ativa */}
      {!loadingAtiva && visitaAtiva && (
        <Alert
          severity="info"
          icon={<FiberManualRecordIcon fontSize="small" sx={{ color: 'info.main' }} />}
          sx={{ mb: 3 }}
          action={
            <Button
              size="small"
              variant="contained"
              color="warning"
              startIcon={<LogoutIcon />}
              onClick={() => setCheckoutOpen(true)}
            >
              Check-out
            </Button>
          }
        >
          <Typography variant="body2" fontWeight={600}>
            Visita em andamento
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Cliente {visitaAtiva.cliente_id} — desde {formatHora(visitaAtiva.checkin_at)}
          </Typography>
        </Alert>
      )}

      {/* Cards de navegação rápida */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card variant="outlined">
            <CardActionArea onClick={() => navigate('/visitas/agenda')}>
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <CalendarMonthIcon sx={{ fontSize: 36, color: 'primary.main', mb: 1 }} />
                <Typography variant="subtitle1" fontWeight={600}>
                  Agenda do Dia
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Planeje e reordene visitas
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card variant="outlined">
            <CardActionArea onClick={() => navigate('/visitas/historico')}>
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <HistoryIcon sx={{ fontSize: 36, color: 'primary.main', mb: 1 }} />
                <Typography variant="subtitle1" fontWeight={600}>
                  Histórico
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Visitas realizadas com filtros
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>

        <Grid item xs={12} sm={4}>
          <Card variant="outlined">
            <CardActionArea onClick={() => navigate('/mapa')}>
              <CardContent sx={{ textAlign: 'center', py: 3 }}>
                <MapIcon sx={{ fontSize: 36, color: 'primary.main', mb: 1 }} />
                <Typography variant="subtitle1" fontWeight={600}>
                  Mapa / Rota
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Check-in GPS e roteirização
                </Typography>
              </CardContent>
            </CardActionArea>
          </Card>
        </Grid>
      </Grid>

      {/* Visitas de hoje */}
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Hoje
      </Typography>
      <Divider sx={{ mb: 1 }} />

      {loadingAgenda ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={24} />
        </Box>
      ) : agenda.length === 0 ? (
        <Box textAlign="center" py={4}>
          <Typography color="text.secondary" variant="body2">
            Nenhuma visita agendada para hoje.
          </Typography>
          <Button size="small" sx={{ mt: 1 }} onClick={() => navigate('/visitas/agenda')}>
            Adicionar visita à agenda
          </Button>
        </Box>
      ) : (
        <List disablePadding>
          {agenda
            .slice()
            .sort((a, b) => a.ordem_dia - b.ordem_dia)
            .map((visita, idx) => (
              <ListItem key={visita.id} divider={idx < agenda.length - 1} sx={{ px: 0 }}>
                <ListItemText
                  primary={
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2" fontWeight={500}>
                        {visita.ordem_dia}. {visita.cliente_nome ?? `Cliente ${visita.cliente_id}`}
                      </Typography>
                    </Stack>
                  }
                  secondary={
                    <Typography variant="caption" color="text.secondary">
                      {formatHora(visita.data_planejada)} · {visita.tipo_visita}
                      {visita.observacoes ? ` · ${visita.observacoes}` : ''}
                    </Typography>
                  }
                />
                <ListItemSecondaryAction>
                  <Chip
                    label={STATUS_LABEL[visita.status] ?? visita.status}
                    size="small"
                    color={STATUS_COLOR[visita.status] ?? 'default'}
                    sx={{ fontSize: '0.7rem', height: 22 }}
                  />
                </ListItemSecondaryAction>
              </ListItem>
            ))}
        </List>
      )}

      {/* Modal de checkout da visita ativa */}
      {visitaAtiva && (
        <CheckoutModal
          open={checkoutOpen}
          onClose={() => setCheckoutOpen(false)}
          onSuccess={handleCheckoutSuccess}
          visita={visitaAtiva}
        />
      )}
    </Box>
  );
}
